import { SingleTab } from '../SingleTab.component';
import { MeteoSingleTabButton } from './MeteoSingleTabButton.component';
import React, { ComponentProps, ComponentPropsWithRef, FC, forwardRef, PropsWithChildren, useCallback } from 'react';
import RadarMobileButton from '@components/buttons/RadarMobileButton/RadarMobileButton.component';
import { Tabs } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { PlcColor } from '@styles/colors';
import classnames from 'classnames';

interface Props extends ComponentPropsWithRef<'div'> {
  activeId: number;
  isForHourByHour?: boolean;
  setActiveId?: (id: number) => void;
  withBody?: boolean;
  children: JSX.Element[];
  tabSeoVariant?: string;
}

const StyledTabs = withStyles({
  indicator: {
    backgroundColor: PlcColor.GREEN,
    height: '3px',
  },
  root: {
    padding: '0 !important',
    width: 'auto !important',
  },
  scrollButtons: {
    '&.Mui-disabled': {
      '& svg': {
        color: PlcColor.GREY_CLOUD_MEDIUM,
        stroke: PlcColor.GREY_CLOUD_MEDIUM,
      },
      opacity: 0.3,
    },
    '&.MuiTabs-scrollButtons svg': {
      border: 'none',
      color: PlcColor.GREEN,
      stroke: PlcColor.GREEN,
      strokeWidth: '1',
      width: '35px',
    },
  },
})(Tabs);

type TabGroupElements = {
  labels: React.ReactElement[];
  bodies: React.ReactElement[];
};

const MeteoTabGroup: FC<PropsWithChildren<Props>> = forwardRef(
  (
    { activeId, isForHourByHour = true, setActiveId, children, className, withBody = true, tabSeoVariant, ...rest },
    tabGroupRef
  ) => {
    const handleChange = useCallback(
      (_: any, newValue: number) => {
        setActiveId?.(newValue);
      },
      [setActiveId]
    );
    const { labels, bodies } = children.reduce(
      (acc: TabGroupElements, child, i) => {
        const {
          label,
          subLabel,
          labelColor,
          disabled,
          className: tabClassName,
          notI18n,
          ...restChild
        } = child.props as ComponentProps<typeof SingleTab>;

        const innerTabClassName = classnames('inner-tab', tabClassName, activeId === i ? 'active' : 'non-active');
        acc.labels.push(
          <MeteoSingleTabButton
            key={label}
            disabled={disabled}
            label={`${label} ${subLabel}`}
            labelColor={labelColor}
            notI18n={notI18n}
            variant={tabSeoVariant}
          />
        );
        acc.bodies.push(<div key={label} className={innerTabClassName} {...restChild} />);
        return acc;
      },
      {
        bodies: [],
        labels: [],
      }
    );
    const rootClassName = classnames('tab-group', className);

    return (
      <div ref={tabGroupRef} className={rootClassName} {...rest}>
        <StyledTabs onChange={handleChange} scrollButtons="auto" value={activeId} variant="scrollable">
          {labels}
          {!isForHourByHour && <RadarMobileButton />}
        </StyledTabs>
        {withBody && (
          <div className="tab-body" style={{ padding: '0px' }}>
            {bodies.map((tabBody, i) => (i === activeId ? tabBody : undefined))}
          </div>
        )}
      </div>
    );
  }
);

export { MeteoTabGroup };
